import { LayoutSearchParams } from "@/shared/types/layout";
import { Icon, IconProps, Styles } from "@/shared/coralreef";
import { useMatchRoute } from "@tanstack/react-router";
import { important, percent, px } from "csx";
import { Box, Content, Horizontal, Stretch, Vertical } from "gls";
import { FC } from "react";
import { usePermissions } from "shared/hooks/usePermissions";
import { style } from "typestyle";
import { Badge } from "../badge/Badge";

export const SidebarItem: FC<{
  isCollapsed?: boolean;
  hideTextWhenCollapsed?: boolean;
  icon?: IconProps["icon"];
  text: string;
  collapsedText?: string;
  alertCount?: number;
  navigateTo?: string;
  navigateParams?: Record<string, number>;
  navigateSearch?: (search: LayoutSearchParams) => LayoutSearchParams;
  onClick?: () => void;
  permissionName?: string;
  "data-testid"?: string;
}> = ({
  isCollapsed,
  hideTextWhenCollapsed,
  icon,
  text,
  collapsedText,
  alertCount,
  navigateTo,
  navigateParams,
  navigateSearch,
  onClick,
  permissionName,
  "data-testid": dataTestId,
}) => {
  const isHighlighted =
    useMatchRoute()({
      to: navigateTo,
      params: navigateParams
        ? { dashboardId: String(navigateParams?.dashboardId) }
        : undefined,
    }) && navigateTo;

  if (permissionName != null && permissionName != undefined) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(permissionName)) {
      return <></>;
    }
  }

  return (
    <Horizontal
      spacing={10}
      horizontalAlign={isCollapsed ? "center" : "left"}
      verticalAlign="center"
      className={style({
        cursor: navigateTo ? "pointer" : "default",
        color: isHighlighted ? Styles.Colours.primary : Styles.Colours.white,
        overflow: "hidden",
      })}
      padding={[0, 0, px(14), isCollapsed ? 0 : px(20)]}
      width={percent(100)}
      data-testid={dataTestId}
      onClick={() => {
        onClick && onClick();

        if (!navigateTo) return;
        let search: Record<string, unknown> | undefined;
        if (typeof navigateSearch === "function") {
          try {
            search = (navigateSearch as unknown as (s: object) => object)(
              {},
            ) as Record<string, unknown>;
          } catch {
            /* ignore */
          }
        }
        let url = navigateTo;
        if (search) {
          const qs = new URLSearchParams();
          for (const [k, v] of Object.entries(search)) {
            if (v === undefined || v === null) continue;
            qs.set(k, typeof v === "string" ? v : JSON.stringify(v));
          }
          const s = qs.toString();
          if (s) url += (url.includes("?") ? "&" : "?") + s;
        }
        if (window.location.pathname + window.location.search === url) return;
        window.history.pushState({}, "", url);
        window.dispatchEvent(
          new PopStateEvent("popstate", { state: window.history.state }),
        );
      }}
    >
      <Vertical
        spacing={px(5)}
        sizing={isCollapsed ? "stretch" : "content"}
        horizontalAlign="center"
        width={isCollapsed ? percent(100) : "auto"}
      >
        {icon && (
          <Content className={iconContainer}>
            <Icon icon={icon} className={style({ width: important(px(20)) })} />
            {(alertCount || 0) > 0 && isCollapsed && (
              <Box className={alertDot} />
            )}
          </Content>
        )}
        {isCollapsed && !hideTextWhenCollapsed && (
          <Stretch
            horizontalAlign="center"
            padding={[0, px(5)]}
            width={percent(100)}
            className={style({ boxSizing: "border-box" })}
          >
            <span className={collapsedTextContainer}>
              {collapsedText || text}
            </span>
          </Stretch>
        )}
      </Vertical>
      {!isCollapsed && (
        <Horizontal
          sizing="stretch"
          height={percent(100)}
          spacing={0}
          className={style({ overflow: "hidden" })}
        >
          <span
            className={style({
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            })}
          >
            {text}
          </span>
        </Horizontal>
      )}

      {!isCollapsed && alertCount && <Badge>{alertCount}</Badge>}
    </Horizontal>
  );
};

const collapsedTextContainer = style({
  fontSize: px(10),
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: "normal",
});

const iconContainer = style({ position: "relative" });

const alertDot = style({
  position: "absolute",
  width: px(8),
  height: px(8),
  background: Styles.Colours.red,
  borderRadius: percent(50),
  border: `3px solid ${Styles.Colours.blue}`,
  top: px(-5),
  right: px(-5),
});
